"use client";
import React, { useRef, useEffect, useMemo } from "react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@shared/src/components/ui/dropdown-menu";
import { APP_VERSION } from "@shared/src/utils/constants";

export function CustomDropdown({
  trigger,
  content,
  dropdownId,
  openDropdown,
  setOpenDropdown,
  showVersion,
}: {
  trigger: React.ReactNode;
  content: React.ReactElement<{ onCloseParentDropdown?: () => void }> | React.ReactNode;
  dropdownId: string;
  openDropdown: string | null;
  setOpenDropdown: (id: string | null) => void;
  showVersion?: boolean;
}) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const appVersion = useMemo(() => APP_VERSION || "dev", []);

  const clearTimeouts = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    clearTimeouts();
    const target = e.target as HTMLElement;
    const isOverTrigger = target.closest(`[data-dropdown="${dropdownId}"]`);
    if (isOverTrigger) setOpenDropdown(dropdownId);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget) {
      setOpenDropdown(null);
      return;
    }
    const isMovingToContent = relatedTarget.closest('[role="menu"]');
    const isMovingToAnotherDropdown = relatedTarget.closest("[data-dropdown]");
    if (!isMovingToContent && !isMovingToAnotherDropdown) {
      timeoutRef.current = setTimeout(() => setOpenDropdown(null), 100);
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
  };

  useEffect(() => () => clearTimeouts(), []);

  const renderedContent =
    React.isValidElement(content) && (content.type as any)?.name === "Menu"
      ? React.cloneElement(content as any, { onCloseParentDropdown: () => setOpenDropdown(null) })
      : content;

  return (
    <div
      ref={containerRef}
      data-dropdown={dropdownId}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={openDropdown === dropdownId ? "z-50" : ""}
    >
      <DropdownMenu modal={false} open={openDropdown === dropdownId}>
        <DropdownMenuTrigger asChild>
          <div onClick={handleTriggerClick} role="button" tabIndex={0}>
            {trigger}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          variant="menu"
          size="lg"
          radius="2xl"
          className="relative z-50"
          sideOffset={0}
          collisionPadding={20}
          onEscapeKeyDown={() => setOpenDropdown(null)}
          onInteractOutside={e => {
            const target = e.target as HTMLElement;
            const isMovingToDropdown = target.closest("[data-dropdown]");
            if (!isMovingToDropdown) setOpenDropdown(null);
          }}
          onMouseEnter={clearTimeouts}
          onClick={e => e.stopPropagation()}
        >
          <div className="space-y-3">
            {renderedContent}
            {showVersion && (
              <div className="pt-2 mt-1 border-t border-surfaces-bg-tertiary flex items-center justify-between text-micro text-text-tertiary">
                <span className="font-medium">Version</span>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator?.clipboard) navigator.clipboard.writeText(appVersion.toString()).catch(() => {});
                  }}
                  className="px-2 py-0.5 rounded-md bg-surfaces-bg-secondary hover:bg-surfaces-bg-hover text-text-secondary hover:text-text-primary transition-colors text-[10px] font-semibold"
                  title="Copy version"
                >
                  {appVersion}
                </button>
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
